import { makeAutoObservable, runInAction } from 'mobx';
import { io, Socket } from 'socket.io-client';
import { gatewayConfig, SymbolPriceUpdateInterface } from '@app/common';
import { isAfter } from 'date-fns';
import type { RootStore } from './root-store';
import { CurrencyRateMobxDto } from '../mobx/dtos/currency/currency-rate-mobx-dto';

export class SymbolPriceUpdateStore {
    rootStore: RootStore;

    private symbolPriceUpdateSocket: Socket | null;

    private readonly lastSymbolPriceBySymbolId: Map<string, SymbolPriceUpdateInterface> = new Map();

    private readonly symbolPriceBuffer = new Map<string, SymbolPriceUpdateInterface>();

    private flushTimer: NodeJS.Timeout | null = null;

    currencyRatesBySymbol: Map<string, CurrencyRateMobxDto> = new Map();

    private subscribedSymbolIds?: string[];

    constructor(rootStore: RootStore) {
        this.rootStore = rootStore;

        makeAutoObservable(this, {
            rootStore: false,
        });
    }

    public initConnect(): void {
        if (!this.symbolPriceUpdateSocket) {
            const jwtToken = this.rootStore.cookieService.getJwtToken();
            if (jwtToken) {
                runInAction(() => {
                    this.symbolPriceUpdateSocket = io(
                        `${process.env.NEXT_PUBLIC_SOCKET_URL}/${gatewayConfig().channels.symbolPriceUpdate}`,
                        {
                            auth: {
                                authorization: jwtToken,
                            },
                            reconnection: true,
                            secure: true,
                            reconnectionAttempts: Infinity,
                            reconnectionDelay: 1000,
                            transports: ['websocket'],
                        },
                    );

                    this.symbolPriceUpdateSocket.on('connect', () => {
                        if (this.subscribedSymbolIds?.length) {
                            this.subscribeSymbolPriceUpdate(this.subscribedSymbolIds);
                        }
                    });

                    this.symbolPriceUpdateSocket.on('disconnect', (reason) => {
                        // eslint-disable-next-line
                        console.warn('🔌 Socket disconnected:', reason);
                    });

                    this.symbolPriceUpdateSocket.on('reconnect', (attempt) => {
                        // eslint-disable-next-line
                        console.log(`🔁 Socket reconnected after ${attempt} attempts`);
                        if (this.subscribedSymbolIds?.length) {
                            this.subscribeSymbolPriceUpdate(this.subscribedSymbolIds);
                        }
                    });

                    this.symbolPriceUpdateSocket.on(
                        gatewayConfig().events.symbolPriceUpdate.pub.data,
                        (data: SymbolPriceUpdateInterface) => {
                            runInAction(() => {
                                this.symbolPriceBuffer.set(data.symbolId, data);
                            });
                        },
                    );
                });
            }
        }
    }

    public subscribeSymbolPriceUpdate(symbolIds: string[]): void {
        this.startFlushing();
        this.symbolPriceUpdateSocket?.emit(gatewayConfig().events.symbolPriceUpdate.sub.subscribe, {
            symbolIds,
        });
        this.subscribedSymbolIds = symbolIds;
    }

    public unsubscribeSymbolPriceUpdate(): void {
        this.resetFlushing();
        if (this.subscribedSymbolIds) {
            this.symbolPriceUpdateSocket?.emit(gatewayConfig().events.symbolPriceUpdate.sub.unsubscribe, {
                symbolIds: this.subscribedSymbolIds,
            });
        }
    }

    public getLastSymbolPriceBySymbolId(symbolId: string): SymbolPriceUpdateInterface | undefined {
        return this.lastSymbolPriceBySymbolId.get(symbolId);
    }

    public getTodayProfitBySymbolId(
        symbolId: string,
        oldValue: number,
        amount: number,
        date: Date,
        priceUpdate?: SymbolPriceUpdateInterface,
    ): number {
        const actualPrice = priceUpdate ?? this.rootStore.symbolPriceUpdateStore.getLastSymbolPriceBySymbolId(symbolId);
        const portfolio = this.rootStore.portfolioStore.selectedPortfolio;

        if (actualPrice?.close && portfolio && actualPrice && isAfter(new Date(actualPrice.timestamp), date)) {
            const currencyRate = this.currencyRatesBySymbol.get(
                `${actualPrice.currency}${portfolio.portfolioHistoryCurrencyCode}`,
            );
            if (currencyRate || actualPrice.currency === portfolio.portfolioHistoryCurrencyCode) {
                const newValue =
                    (actualPrice.close / (actualPrice.todaySplit ?? 1)) * amount * (currencyRate?.rate ?? 1);
                return newValue - oldValue;
            }
        }

        return 0;
    }

    async fetchCurrencyRates(symbols: string[]): Promise<void> {
        if (symbols.length) {
            const result =
                await this.rootStore.apiClient.currencyController.currencyControllerGetAllRatesByCurrencyCode({
                    symbols,
                });

            runInAction(() => {
                result.forEach((currencyRate) => {
                    this.currencyRatesBySymbol.set(currencyRate.symbol, currencyRate);
                });
            });
        }
    }

    private resetFlushing() {
        if (this.flushTimer) {
            clearInterval(this.flushTimer);
            this.flushTimer = null;
        }
    }

    private startFlushing() {
        this.resetFlushing();
        this.flushTimer = setInterval(() => {
            if (this.symbolPriceBuffer.size === 0) return;

            runInAction(() => {
                // eslint-disable-next-line no-restricted-syntax
                for (const [symbolId, update] of this.symbolPriceBuffer) {
                    this.rootStore.statisticStore.ratiosStore.setLiveProfit(symbolId, update);
                    this.lastSymbolPriceBySymbolId.set(symbolId, update);
                }
                this.symbolPriceBuffer.clear();
            });
        }, 2000);
    }
}
